import type {
  Amendment,
  AmendmentStatus,
  CooperationEvent,
  Discussion,
  Firm,
  GovernanceProposal,
  LegislationFile,
  LobbyingProject,
  PracticeArea,
  ProposalCategory,
  Role,
  TechnicalRole,
  User,
  UserMemberCategory,
} from "./types";

export const adminRoles: Role[] = ["Super Admin", "Governance Admin"];
export const affiliationViewerRoles: Role[] = ["Super Admin", "Governance Admin", "Coordination Committee", "Membership and Balance Committee"];
export const fullMemberRoles: Role[] = ["Full Member Representative", "Alternate Representative"];
export const technicalRoles: TechnicalRole[] = ["public_site_editor", "workspace_manager", "administrator", "superadministrator"];
export const userMemberCategories: UserMemberCategory[] = ["observer", "contributor", "associate_member", "alternate_representative", "full_member_representative"];
export const firmRepresentativeTypes = ["principal", "alternate"] as const;
export const sensitiveProposalCategories: ProposalCategory[] = [
  "admission",
  "larger boutique admission",
  "charter amendment",
  "suspension",
  "exclusion",
  "conflict decision",
];

export function isAdmin(user: Pick<User, "role">) {
  return hasTechnicalRole(user, "administrator");
}

export function isFullMemberUser(user: Pick<User, "role" | "memberCategory">) {
  return memberCategoryForUser(user) === "full_member_representative" || memberCategoryForUser(user) === "alternate_representative";
}

export function isFirmRepresentative(user: Pick<User, "representativeType">) {
  return firmRepresentativeTypes.includes(user.representativeType as "principal" | "alternate");
}

export function canViewAdmin(user: Pick<User, "role">) {
  return isAdmin(user);
}

// Observers can sign in and browse network projects + legislative action, but not the
// members directory, governance, votes, or expertise exchange until admitted as members.
export function isObserverUser(user: Pick<User, "role"> & Partial<Pick<User, "memberCategory" | "representativeType">>) {
  return memberCategoryForUser(user) === "observer";
}

export function canUsePublicSiteEditor(user: Pick<User, "role" | "roles">) {
  return hasTechnicalRole(user, "public_site_editor");
}

export function canReviewPublicSiteChanges(user: Pick<User, "role" | "roles">) {
  return hasTechnicalRole(user, "superadministrator");
}

export function hasTechnicalRole(user: Pick<User, "role"> & Partial<Pick<User, "roles">>, role: TechnicalRole) {
  return technicalRolesForUser(user).includes(role);
}

export function technicalRolesForUser(user: Pick<User, "role"> & Partial<Pick<User, "roles">>): TechnicalRole[] {
  const assigned = user.roles ?? [];
  const inherited = legacyTechnicalRoles(user.role);
  const combined = new Set<TechnicalRole>([...assigned, ...inherited]);
  if (combined.has("superadministrator")) {
    combined.add("administrator");
    combined.add("workspace_manager");
    combined.add("public_site_editor");
  }
  if (combined.has("administrator")) {
    combined.add("workspace_manager");
    combined.add("public_site_editor");
  }
  return [...combined];
}

export function memberCategoryForUser(user: Pick<User, "role"> & Partial<Pick<User, "memberCategory" | "representativeType">>): UserMemberCategory {
  if (user.memberCategory) return user.memberCategory;
  if (user.role === "Full Member Representative") return "full_member_representative";
  if (user.role === "Alternate Representative") return "alternate_representative";
  if (user.role === "Associate Member") return "associate_member";
  if (user.role === "Expert Contributor") return "contributor";
  if (user.role === "Observer") return "observer";
  if (user.representativeType === "principal") return "full_member_representative";
  if (user.representativeType === "alternate") return "alternate_representative";
  if (user.representativeType === "participant") return "contributor";
  return "observer";
}

export function memberCategoryLabel(category: UserMemberCategory) {
  if (category === "full_member_representative") return "Full member representative";
  if (category === "alternate_representative") return "Alternate representative";
  if (category === "associate_member") return "Associate member";
  if (category === "contributor") return "Contributor";
  return "Observer";
}

export function technicalRoleLabel(role: TechnicalRole) {
  if (role === "public_site_editor") return "Public site editor";
  if (role === "workspace_manager") return "Workspace manager";
  if (role === "administrator") return "Administrator";
  return "Superadministrator";
}

function legacyTechnicalRoles(role: Role): TechnicalRole[] {
  if (role === "Super Admin") return ["superadministrator"];
  if (role === "Governance Admin") return ["administrator"];
  if (role === "Coordination Committee" || role === "Membership and Balance Committee" || role === "Cooperation Panel") return ["workspace_manager"];
  return [];
}

export function canViewFirmProfile(viewer: User, firm: Firm) {
  if (firm.status !== "active" && !isAdmin(viewer)) return false;
  return true;
}

export function canEditFirmProfile(viewer: User, firm: Firm) {
  return isAdmin(viewer) || (viewer.firmId === firm.id && isFirmRepresentative(viewer));
}

export function canViewAffiliationGroup(viewer: User, firm: Firm) {
  return hasTechnicalRole(viewer, "workspace_manager") || affiliationViewerRoles.includes(viewer.role) || (viewer.firmId === firm.id && isFirmRepresentative(viewer));
}

export function canViewDiscussion(viewer: User, discussion: Discussion) {
  if (discussion.visibility === "admin") return isAdmin(viewer);
  if (discussion.visibility === "firm") return viewer.firmId === discussion.createdByFirmId || isAdmin(viewer);
  if (discussion.visibility === "committee") return hasTechnicalRole(viewer, "workspace_manager") || affiliationViewerRoles.includes(viewer.role);
  return viewer.active;
}

export function canEditDiscussion(viewer: User, discussion: Discussion) {
  return isAdmin(viewer) || viewer.id === discussion.createdByUserId || (viewer.firmId === discussion.createdByFirmId && isFirmRepresentative(viewer));
}

export function canViewLegislationProject(viewer: User, file: LegislationFile) {
  return viewer.active && (file.status !== "closed" || isAdmin(viewer));
}

export function canEditLegislationProject(viewer: User, file: LegislationFile) {
  return isAdmin(viewer) || (Boolean(file.ownerFirmId) && viewer.firmId === file.ownerFirmId && isFirmRepresentative(viewer));
}

export function canViewCooperationEvent(viewer: User, event: CooperationEvent) {
  return isAdmin(viewer) || hasTechnicalRole(viewer, "workspace_manager") || viewer.firmId === event.initiatingFirmId || viewer.firmId === event.receivingFirmId || viewer.role === "Cooperation Panel";
}

export function canMutateCooperationEvent(viewer: User, event: CooperationEvent) {
  return canViewCooperationEvent(viewer, event) && (isAdmin(viewer) || isFirmRepresentative(viewer) || hasTechnicalRole(viewer, "workspace_manager") || viewer.role === "Cooperation Panel");
}

export function filterMembers(
  firms: Firm[],
  practiceLinks: { firmId: string; practiceAreaId: string }[],
  options: { search?: string; jurisdictions?: string[]; practiceAreaIds?: string[]; matchAllPracticeAreas?: boolean; statuses?: Firm["status"][] },
) {
  const search = options.search?.trim().toLowerCase() ?? "";
  const selectedPractices = options.practiceAreaIds ?? [];
  const selectedJurisdictions = options.jurisdictions ?? [];
  const allowedStatuses = options.statuses ?? ["active"];

  return firms.filter((firm) => {
    if (!allowedStatuses.includes(firm.status)) return false;
    if (search && !`${firm.name} ${firm.jurisdiction}`.toLowerCase().includes(search)) return false;
    if (selectedJurisdictions.length > 0 && !selectedJurisdictions.includes(firm.jurisdiction)) return false;
    if (selectedPractices.length > 0) {
      const firmAreas = practiceLinks.filter((link) => link.firmId === firm.id).map((link) => link.practiceAreaId);
      const matches = options.matchAllPracticeAreas
        ? selectedPractices.every((id) => firmAreas.includes(id))
        : selectedPractices.some((id) => firmAreas.includes(id));
      if (!matches) return false;
    }
    return true;
  });
}

export function getFirmPracticeAreas(firmId: string, links: { firmId: string; practiceAreaId: string }[], areas: PracticeArea[]) {
  const ids = links.filter((link) => link.firmId === firmId).map((link) => link.practiceAreaId);
  return areas.filter((area) => ids.includes(area.id) && area.active);
}

export function getUserPracticeAreas(userId: string, links: { userId: string; practiceAreaId: string }[], areas: PracticeArea[]) {
  const ids = links.filter((link) => link.userId === userId).map((link) => link.practiceAreaId);
  return areas.filter((area) => ids.includes(area.id) && area.active);
}

export function validatePracticeAreaSelection(areaIds: string[]) {
  const uniqueIds = new Set(areaIds);
  if (uniqueIds.size !== areaIds.length) return "Practice areas must be unique.";
  if (areaIds.length > 10) return "Select up to 10 practice areas.";
  return null;
}

export function validateAffiliationOther(input: { name?: string; affiliationType?: string; explanation?: string }) {
  if (!input.name?.trim()) return "Affiliation group name is required.";
  if (!input.affiliationType?.trim()) return "Affiliation type is required.";
  if (!input.explanation?.trim()) return "A short explanation is required.";
  if (input.explanation.length > 300) return "Explanation must be 300 characters or fewer.";
  return null;
}

export function calculateOwnVotingUnits(participationBonus: number) {
  return 100 + Math.max(0, Math.min(50, participationBonus));
}

export function delegationHoldingCap(fullMemberCount: number) {
  if (fullMemberCount >= 40) return 0.1;
  if (fullMemberCount >= 30) return 0.125;
  if (fullMemberCount >= 20) return 0.15;
  if (fullMemberCount >= 15) return 0.2;
  if (fullMemberCount >= 5) return 0.25;
  return 0;
}

export function calculateEffectiveVotingUnits(input: {
  fullMemberCount: number;
  ownVotingUnits: number;
  delegatedUnits: number;
  proposalCategory: ProposalCategory;
}) {
  if (sensitiveProposalCategories.includes(input.proposalCategory)) {
    return {
      ownVotingUnits: input.ownVotingUnits,
      delegatedUnitsCounted: 0,
      totalEffectiveUnits: input.ownVotingUnits,
      capApplied: true,
    };
  }

  const totalNetworkUnits = input.fullMemberCount * 150;
  const capUnits = totalNetworkUnits * delegationHoldingCap(input.fullMemberCount);
  const maxTotal = Math.max(input.ownVotingUnits, capUnits);
  const delegatedUnitsCounted = Math.max(0, Math.min(input.delegatedUnits, maxTotal - input.ownVotingUnits));
  return {
    ownVotingUnits: input.ownVotingUnits,
    delegatedUnitsCounted,
    totalEffectiveUnits: input.ownVotingUnits + delegatedUnitsCounted,
    capApplied: delegatedUnitsCounted < input.delegatedUnits,
  };
}

export function validateAmendmentSubmission(input: {
  proposal: Pick<GovernanceProposal, "amendmentOpensAt" | "amendmentClosesAt">;
  now: number;
  targetAmendmentId?: string | null;
  targetSectionId?: string;
  proposedText?: string;
  rationale?: string;
}): { status: AmendmentStatus; reason: string | null } {
  if (input.targetAmendmentId) return { status: "auto_rejected", reason: "Amendments to amendments are not allowed." };
  if (input.now < input.proposal.amendmentOpensAt || input.now > input.proposal.amendmentClosesAt) {
    return { status: "auto_rejected", reason: "The amendment window is closed." };
  }
  if (!input.targetSectionId?.trim()) return { status: "auto_rejected", reason: "A target section is required." };
  if (!input.proposedText?.trim()) return { status: "auto_rejected", reason: "Proposed text is required." };
  if (!input.rationale?.trim()) return { status: "auto_rejected", reason: "A rationale is required." };
  return { status: "provisionally_admissible", reason: null };
}

export function canObjectToAmendment(viewer: User, amendment: Amendment, supportingFullMemberFirmIds: string[]) {
  if (!isFullMemberUser(viewer) || !viewer.firmId) return false;
  if (viewer.firmId === amendment.sponsorFirmId) return false;
  return supportingFullMemberFirmIds.some((firmId) => firmId !== viewer.firmId && firmId !== amendment.sponsorFirmId);
}

export function finalVoteRequirement(proposal: GovernanceProposal) {
  if (!proposal.consolidatedText?.trim()) return "Final vote requires consolidated text.";
  return null;
}

export function validateCooperationSummary(text: string) {
  const forbidden = /\b(client|matter value|value of the matter|fee|fees|revenue|invoice|privileged|damages of)\b/i;
  return forbidden.test(text) ? "Cooperation records must not include client names, values, fees, or privileged details." : null;
}

export function validateCooperationEventDraft(event: Partial<CooperationEvent>) {
  if (!event.receivingFirmId) return "Receiving firm is required.";
  if (!event.level) return "Cooperation level is required.";
  if (!event.practiceAreaId) return "Practice area is required.";
  if (!event.jurisdictionFrom || !event.jurisdictionTo) return "Jurisdictions are required.";
  if (!event.cooperationType) return "Cooperation type is required.";
  if (!event.consentConfirmed) return "Consent must be confirmed.";
  const summaryCheck = validateCooperationSummary(`${event.cooperationType ?? ""} ${event.handlingConfirmation ?? ""}`);
  if (summaryCheck) return summaryCheck;
  return null;
}

export function canLogExternalCommunication(project: Pick<LobbyingProject, "mandateStatus">) {
  return project.mandateStatus === "approved" || project.mandateStatus === "emergency";
}

export function createAuditEntry(actionType: string, entityType: string, entityId: string, actorUserId?: string | null) {
  return {
    actorUserId: actorUserId ?? null,
    actionType,
    entityType,
    entityId,
    createdAt: Date.now(),
  };
}
